// web/src/App.tsx — שלד האפליקציה (D-245).
// 🔑 מי רואה מה — לפי /me.is_staff (השרת מחליט). צוות → כלי הניהול · לקוחה → האזור האישי.
// ⛔ הסתרת מסך איננה אבטחה: כל נתיב בשרת בודק הרשאה בעצמו (403). כאן זו רק הדרך הנכונה לכל אחת.
// 🆕 27.09 — פיצול לפי מסלול (lazy): הקובץ הראשי הגיע ל-624KB וגדל בכל מסך. 🔑 לקוחה ⛔ טוענת את כלי הניהול,
//    ועתידה טוענת כל מסך רק כשנכנסת אליו. הכניסה והשלד נשארים בקובץ הראשי (המסך הראשון בלי המתנה נוספת).
import { lazy, Suspense, type ComponentType } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { Login } from "./pages/Login";
import { StaffHome, StaffShell } from "./pages/StaffShell";

// הקבצים מייצאים בשם (⛔ default) — העטיפה ממירה, כדי ש-lazy יקבל רכיב.
function page<M, K extends keyof M>(load: () => Promise<M>, name: K) {
  return lazy(async () => ({ default: (await load())[name] as unknown as ComponentType }));
}

const CalendarWeek = page(() => import("./pages/staff/CalendarWeek"), "CalendarWeek");
const OccurrenceCard = page(() => import("./pages/staff/OccurrenceCard"), "OccurrenceCard");
const WhatsChanged = page(() => import("./pages/staff/WhatsChanged"), "WhatsChanged");
const Availability = page(() => import("./pages/staff/settings/Availability"), "Availability");
const ActivityTypes = page(() => import("./pages/staff/settings/ActivityTypes"), "ActivityTypes");
const Policies = page(() => import("./pages/staff/settings/Policies"), "Policies");
const PassTypes = page(() => import("./pages/staff/settings/PassTypes"), "PassTypes");
const CreateOccurrence = page(() => import("./pages/staff/CreateOccurrence"), "CreateOccurrence");
const ClientPasses = page(() => import("./pages/staff/ClientPasses"), "ClientPasses");
const PassesSearch = page(() => import("./pages/staff/ClientPasses"), "PassesSearch");
const People = page(() => import("./pages/crm/People"), "People");
const PersonCard = page(() => import("./pages/crm/PersonCard"), "PersonCard");
const Leads = page(() => import("./pages/crm/Leads"), "Leads");
const Tasks = page(() => import("./pages/crm/Tasks"), "Tasks");
const Segments = page(() => import("./pages/crm/Segments"), "Segments");
const Events = page(() => import("./pages/crm/Events"), "Events");
const Merges = page(() => import("./pages/crm/Merges"), "Merges");
const Deletion = page(() => import("./pages/crm/Deletion"), "Deletion");
const Users = page(() => import("./pages/crm/Users"), "Users");
const Import = page(() => import("./pages/crm/Import"), "Import");
const StudioQueue = page(() => import("./pages/studio/Queue"), "Queue");
const StudioReflections = page(() => import("./pages/studio/Reflections"), "Reflections");
const StudioSent = page(() => import("./pages/studio/Reflections"), "SentList");
const StudioCourses = page(() => import("./pages/studio/Courses"), "Courses");
const StudioCourse = page(() => import("./pages/studio/Course"), "Course");
const StudioMedia = page(() => import("./pages/studio/Media"), "MediaLibrary");
const StudioTranscript = page(() => import("./pages/studio/Transcript"), "Transcript");   // 🆕 02.10 — ST-06   // 🆕 02.10 — ST-11
const InboxBroadcast = page(() => import("./pages/inbox/Pulse2"), "Broadcast");          // 🆕 02.10 — MS-06
const InboxBroadcastReport = page(() => import("./pages/inbox/Pulse2"), "BroadcastReportPage");
const InboxSettings = page(() => import("./pages/inbox/Pulse2"), "InboxSettings");      // MS-07
const InboxMetrics = page(() => import("./pages/inbox/Pulse2"), "InboxMetrics");        // MS-08
const StudioSubmission = page(() => import("./pages/studio/Submission"), "Submission");
const StudioQuiz = page(() => import("./pages/studio/QuizReview"), "QuizReview");
const StudioQuestion = page(() => import("./pages/studio/Question"), "Question");
const StudioLessonQa = page(() => import("./pages/studio/LessonQa"), "LessonQa");   // 🆕 18.10 — 355 (ג)
const Observance = page(() => import("./pages/staff/settings/Observance"), "Observance");
const ExceptionsLog = page(() => import("./pages/staff/ExceptionsLog"), "ExceptionsLog");
const Cancellations = page(() => import("./pages/staff/Cancellations"), "Cancellations");
const Offers = page(() => import("./pages/staff/Offers"), "Offers");
const Waitlists = page(() => import("./pages/staff/Waitlists"), "Waitlists");
const Standing = page(() => import("./pages/staff/Standing"), "Standing");       // 🆕 29.09 — CA-24
const Requests = page(() => import("./pages/staff/Requests"), "Requests");       // 🆕 29.09 — CA-22
const MyArea = page(() => import("./pages/my/MyArea"), "MyArea");
const BookSession = page(() => import("./pages/my/BookSession"), "BookSession");
const ChangeBooking = page(() => import("./pages/my/ChangeBooking"), "ChangeBooking");
const MyPasses = page(() => import("./pages/my/MyPasses"), "MyPasses");
const LearnLesson = page(() => import("./pages/learn/Lesson"), "Lesson");
const CourseDone = page(() => import("./pages/learn/CourseDone"), "CourseDone");
const MyCertificates = page(() => import("./pages/learn/CourseDone"), "MyCertificates");
const Inbox = page(() => import("./pages/learn/Inbox"), "Inbox");
const MyCourses = page(() => import("./pages/learn/MyCourses"), "MyCourses");
const CourseMap = page(() => import("./pages/learn/CourseMap"), "CourseMap");
const Profile = page(() => import("./pages/learn/Profile"), "Profile");
const Privacy = page(() => import("./pages/learn/Privacy"), "Privacy");            // 🆕 02.10 — AC-09
const EmailChange = page(() => import("./pages/learn/EmailChange"), "EmailChange");    // 🆕 02.10 — AC-08.e (ציבורי)
const Unsubscribe = page(() => import("./pages/learn/Privacy"), "Unsubscribe");    // 🆕 02.10 — AC-09.b (ציבורי)
const Conversation = page(() => import("./pages/learn/Inbox"), "Conversation");
const VerifyCertificate = page(() => import("./pages/VerifyCertificate"), "VerifyCertificate");
const StaffInbox = page(() => import("./pages/inbox/StaffInbox"), "StaffInbox");
const StaffConversation = page(() => import("./pages/inbox/StaffInbox"), "StaffConversation");
const Desk = page(() => import("./pages/desk/Desk"), "Desk");
const DeskActivity = page(() => import("./pages/desk/DeskAdmin"), "DeskActivity");
const DeskManage = page(() => import("./pages/desk/DeskAdmin"), "DeskManage");
const FeedbackAdmin = page(() => import("./pages/desk/DeskAdmin"), "FeedbackAdmin");
// 🆕 01.10 — שותפים (AF) · פעימה א
const AffiliatesDashboard = page(() => import("./pages/affiliates/Dashboard"), "AffiliatesDashboard");
const AffiliatesCampaigns = page(() => import("./pages/affiliates/Campaigns"), "AffiliatesCampaigns");
const AffiliatesMaster = page(() => import("./pages/affiliates/Campaigns"), "AffiliatesMaster");
const AffiliatesCampaignEdit = page(() => import("./pages/affiliates/CampaignEdit"), "AffiliatesCampaignEdit");
const AffiliatesAgreement = page(() => import("./pages/affiliates/Agreement"), "AffiliatesAgreement");
const AffiliatesPartner = page(() => import("./pages/affiliates/Partner"), "AffiliatesPartner");
const AffiliatesJoin = page(() => import("./pages/affiliates/Partner"), "AffiliatesJoin");
const AffiliatesAttributions = page(() => import("./pages/affiliates/Attributions"), "AffiliatesAttributions");
// 🆕 01.10 — שותפים · פעימה ב
const AffiliatesSettlements = page(() => import("./pages/affiliates/Settlements"), "AffiliatesSettlements");
const AffiliatesExceptions = page(() => import("./pages/affiliates/Exceptions"), "AffiliatesExceptions");
const AffiliatesDecisions = page(() => import("./pages/affiliates/Exceptions"), "AffiliatesDecisions");
const AffiliatesOffsets = page(() => import("./pages/affiliates/Offsets"), "AffiliatesOffsets");
// 🆕 01.10 — שותפים · פעימות ג+ד
const AffiliatesMirror = page(() => import("./pages/affiliates/Frame"), "AffiliatesMirror");
const AffiliatesMessages = page(() => import("./pages/affiliates/Frame"), "AffiliatesMessages");
const AffiliatesAnnual = page(() => import("./pages/affiliates/Frame"), "AffiliatesAnnual");
const AffiliatesLinks = page(() => import("./pages/affiliates/Frame"), "AffiliatesLinks");
const AffiliatesAssets = page(() => import("./pages/affiliates/Frame"), "AffiliatesAssets");
const MyPartner = page(() => import("./pages/my/PartnerArea"), "MyPartner");
const MyPartnerAgreement = page(() => import("./pages/my/PartnerArea"), "MyPartnerAgreement");
const JoinCampaign = page(() => import("./pages/JoinCampaign"), "JoinCampaign");
// 🆕 01.10 — לוח הפעילויות (CA-17 · CA-18 · הגירה 099)
const PublicBoard = page(() => import("./pages/PublicBoard"), "PublicBoard");
const MyBoard = page(() => import("./pages/my/MyBoard"), "MyBoard");
// 🆕 01.10 — מסלולי למידה (LP · הגירה 100)
const Paths = page(() => import("./pages/paths/Paths"), "Paths");
const PathEdit = page(() => import("./pages/paths/PathEdit"), "PathEdit");
const PathMembers = page(() => import("./pages/paths/PathMembers"), "PathMembers");
const MyPath = page(() => import("./pages/my/MyPath"), "MyPath");
// 🆕 03.10 — ב1: מיילים (הגדרות · נוסחים · התור · דופק המנוע · הגירה 117)
const MailSettings = page(() => import("./pages/mail/MailSettings"), "MailSettings");
const Newsletters = page(() => import("./pages/mail/Newsletters"), "Newsletters");          // 🆕 03.10 — ב3
const NewsletterEdit = page(() => import("./pages/mail/Newsletters"), "NewsletterEdit");
const NewsletterResults = page(() => import("./pages/mail/Newsletters"), "NewsletterResults");
const Automations = page(() => import("./pages/mail/Automations"), "Automations");          // 🆕 03.10 — ב5
const AutomationEdit = page(() => import("./pages/mail/Automations"), "AutomationEdit");
const Approvals = page(() => import("./pages/mail/Approvals"), "Approvals");              // 🆕 04.10 — DV-17
const MailEditor = page(() => import("./pages/mail/MailEditor"), "MailEditor");            // 🆕 06.10 - עורך המייל (DV-18)
const MailBrand = page(() => import("./pages/mail/MailBrand"), "MailBrand");               // 🆕 07.10 — DV-20 ערכת המראה
const ContactImport = page(() => import("./pages/mail/ContactImport"), "ContactImport");  // 🆕 04.10 — DV-11 ייבוא (134)
const Setup = page(() => import("./pages/Setup"), "Setup");                                // 🆕 04.10 — ב½ אשף ההקמה
const BusinessOverview = page(() => import("./pages/BusinessOverview"), "BusinessOverview");  // 🆕 03.10 — ב7
const MyPurchases = page(() => import("./pages/my/MyPurchases"), "MyPurchases");              // 🆕 03.10 — ב6
const WhatsappQueue = page(() => import("./pages/mail/WhatsappQueue"), "WhatsappQueue");      // 🆕 03.10 — ב8
const SiteLogin = page(() => import("./pages/SiteLogin"), "SiteLogin");                       // 🆕 04.10 — ב4 כניסה אחת
const Products = page(() => import("./pages/Products"), "Products");                          // 🆕 04.10 — ב4 WP-05
const SiteConnection = page(() => import("./pages/SiteConnection"), "SiteConnection");        // 🆕 04.10 — ב4 WP-06 · WP-08
// 🆕 06.10 — איסוף המלצות (HM v0.2 · הגירה 140): העמוד הציבורי + התיבה של עתידה
const Recommend = page(() => import("./pages/Recommend"), "Recommend");
const Testimonials = page(() => import("./pages/testimonials/Testimonials"), "Testimonials");
const TestimonialOne = page(() => import("./pages/testimonials/Testimonials"), "TestimonialOne");
const TestimonialPage = page(() => import("./pages/testimonials/Testimonials"), "TestimonialPage");

const Loading = () => <p className="muted" style={{ padding: 16 }}>טוען…</p>;

function Gate() {
  const { state } = useAuth();
  if (state.status === "loading") return <main className="center-screen"><p className="muted">רגע…</p></main>;
  if (state.status === "error") {
    return (
      <main className="center-screen">
        <div className="card"><p className="notice notice-err">{state.error.message}</p>
          <button className="btn btn-secondary" onClick={() => location.reload()}>לנסות שוב</button></div>
      </main>
    );
  }
  if (state.status === "signedOut") {
    return <Routes><Route path="*" element={<Login />} /></Routes>;
  }
  if (state.me.is_staff) {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route element={<StaffShell />}>
            {/* 🆕 28.09 — השולחן של עתידה (§14.23) · הצד של מוטי: מעקב · הוספה · הערות לשיפור */}
            <Route path="/desk" element={<Desk />} />
            <Route path="/desk/activity" element={<DeskActivity />} />
            <Route path="/desk/manage" element={<DeskManage />} />
            <Route path="/desk/feedback" element={<FeedbackAdmin />} />
            <Route path="/mail/settings" element={<MailSettings />} />                         {/* 🆕 03.10 — ב1 */}
            <Route path="/mail/brand" element={<MailBrand />} />                               {/* 🆕 07.10 — DV-20 */}
            <Route path="/testimonials" element={<Testimonials />} />                          {/* 🆕 06.10 — HM-10 */}
            <Route path="/testimonials/page" element={<TestimonialPage />} />                  {/* 🆕 06.10 — HM-12 */}
            <Route path="/testimonials/:id" element={<TestimonialOne />} />                    {/* 🆕 06.10 — HM-11 */}
            <Route path="/marketing" element={<Newsletters />} />                              {/* 🆕 03.10 — ב3 */}
            <Route path="/marketing/editor" element={<MailEditor />} /><Route path="/marketing/editor/:cid" element={<MailEditor />} /><Route path="/marketing/editor/step/:aid/:sid" element={<MailEditor />} /><Route path="/marketing/editor/system/:tkey" element={<MailEditor />} />                   {/* 🆕 06.10 - DV-18 */}
            <Route path="/marketing/newsletters/:id" element={<NewsletterEdit />} />
            <Route path="/marketing/newsletters/:id/results" element={<NewsletterResults />} />
            <Route path="/marketing/automations" element={<Automations />} />                  {/* 🆕 03.10 — ב5 */}
            <Route path="/marketing/automations/:id" element={<AutomationEdit />} />
            <Route path="/marketing/approvals" element={<Approvals />} />                      {/* 🆕 04.10 — DV-17 בקשות אישור */}
            <Route path="/marketing/import" element={<ContactImport />} />                     {/* 🆕 04.10 — DV-11 ייבוא אנשי קשר */}
            <Route path="/setup" element={<Setup />} />                                        {/* 🆕 04.10 — ב½ אשף הקמת המערכת */}
            <Route path="/overview" element={<BusinessOverview />} />                          {/* 🆕 03.10 — ב7 */}
            <Route path="/marketing/whatsapp" element={<WhatsappQueue />} />                   {/* 🆕 03.10 — ב8 */}
            <Route path="/products" element={<Products />} />                                  {/* 🆕 04.10 — WP-05 */}
            <Route path="/site-connection" element={<SiteConnection />} />                     {/* 🆕 04.10 — WP-06 · WP-08 */}
            <Route path="/affiliates" element={<AffiliatesDashboard />} />                     {/* 🆕 01.10 — AF-01 */}
            <Route path="/affiliates/campaigns" element={<AffiliatesCampaigns />} />           {/* AF-02 */}
            <Route path="/affiliates/campaigns/new" element={<AffiliatesCampaignEdit />} />    {/* AF-03 */}
            <Route path="/affiliates/campaigns/:id" element={<AffiliatesCampaignEdit />} />
            <Route path="/affiliates/campaigns/:id/agreement" element={<AffiliatesAgreement />} /> {/* AF-04 */}
            <Route path="/affiliates/master" element={<AffiliatesMaster />} />
            <Route path="/affiliates/partners/:id" element={<AffiliatesPartner />} />          {/* AF-05 */}
            <Route path="/affiliates/join" element={<AffiliatesJoin />} />                      {/* AF-06 */}
            <Route path="/affiliates/attributions" element={<AffiliatesAttributions />} />      {/* AF-08 */}
            <Route path="/affiliates/settlements" element={<AffiliatesSettlements />} />        {/* 🆕 AF-09 */}
            <Route path="/affiliates/exceptions" element={<AffiliatesExceptions />} />          {/* AF-10 */}
            <Route path="/affiliates/decisions" element={<AffiliatesDecisions />} />            {/* AF-20 */}
            <Route path="/affiliates/offsets" element={<AffiliatesOffsets />} />                {/* AF-11 */}
            <Route path="/affiliates/partners/:id/mirror" element={<AffiliatesMirror />} />     {/* 🆕 AF-19 */}
            <Route path="/affiliates/messages" element={<AffiliatesMessages />} />              {/* AF-21 */}
            <Route path="/affiliates/annual" element={<AffiliatesAnnual />} />                  {/* AF-22 */}
            <Route path="/affiliates/links" element={<AffiliatesLinks />} />                    {/* AF-13 */}
            <Route path="/affiliates/assets" element={<AffiliatesAssets />} />                  {/* AF-14 */}
            <Route path="/calendar" element={<CalendarWeek />} />
            <Route path="/calendar/occurrence/:id" element={<OccurrenceCard />} />
            <Route path="/calendar/changes" element={<WhatsChanged />} />
            {/* 🆕 26.09 ערב — הגדרות היומן: מה שהופך את השיבוץ העצמי לשמיש (CA-02 · CA-01 · CA-03) */}
            <Route path="/calendar/settings" element={<Navigate to="/calendar/settings/availability" replace />} />
            <Route path="/calendar/settings/availability" element={<Availability />} />
            <Route path="/calendar/settings/types" element={<ActivityTypes />} />
            <Route path="/calendar/settings/policies" element={<Policies />} />
            <Route path="/calendar/settings/pass-types" element={<PassTypes />} />
            <Route path="/calendar/settings/observance" element={<Observance />} />   {/* 🆕 27.09 — CA-04 */}
            <Route path="/calendar/new" element={<CreateOccurrence />} />
            <Route path="/calendar/passes" element={<PassesSearch />} />
            <Route path="/calendar/passes/:personId" element={<ClientPasses />} />
            <Route path="/calendar/waitlists" element={<Waitlists />} />            {/* 🆕 27.09 — CA-10 */}
            <Route path="/calendar/offers" element={<Offers />} />                  {/* 🆕 27.09 — CA-23 */}
            <Route path="/calendar/standing" element={<Standing />} />              {/* 🆕 29.09 — CA-24 */}
            <Route path="/calendar/requests" element={<Requests />} />              {/* 🆕 29.09 — CA-22 */}
            <Route path="/calendar/cancellations" element={<Cancellations />} />    {/* 🆕 27.09 — CA-11 */}
            <Route path="/calendar/exceptions" element={<ExceptionsLog />} />       {/* 🆕 27.09 — CA-25 */}
            {/* 🆕 27.09 — ניהול לקוחות (CR): אנשים · כרטיס · פניות · משימות */}
            <Route path="/people" element={<People />} />
            <Route path="/people/leads" element={<Leads />} />
            <Route path="/people/tasks" element={<Tasks />} />
            <Route path="/people/segments" element={<Segments />} />
            <Route path="/people/events" element={<Events />} />
            <Route path="/people/duplicates" element={<Merges />} />
            <Route path="/people/users" element={<Users />} />
            <Route path="/people/import" element={<Import />} />
            <Route path="/people/:id" element={<PersonCard />} />
            <Route path="/people/:id/delete" element={<Deletion />} />
            {/* 🆕 28.09 — תיבת ההודעות, צד הצוות (MS-04 · MS-05) */}
            <Route path="/inbox" element={<StaffInbox />} />
            <Route path="/inbox/new" element={<InboxBroadcast />} />                 {/* 🆕 02.10 — MS-06 */}
            <Route path="/inbox/broadcasts/:id" element={<InboxBroadcastReport />} />
            <Route path="/inbox/settings" element={<InboxSettings />} />             {/* MS-07 */}
            <Route path="/inbox/metrics" element={<InboxMetrics />} />               {/* MS-08 */}
            <Route path="/inbox/:id" element={<StaffConversation />} />
            {/* 🆕 27.09 — הסטודיו (ST): תור הבדיקה · קורסים · טיוטה ופרסום */}
            <Route path="/studio" element={<StudioQueue />} />
            <Route path="/studio/reflections" element={<StudioReflections />} />
            <Route path="/studio/sent" element={<StudioSent />} />
            <Route path="/studio/courses" element={<StudioCourses />} />
            <Route path="/studio/courses/:id" element={<StudioCourse />} />
            <Route path="/studio/media" element={<StudioMedia />} />
          <Route path="/studio/lessons/:id/transcript" element={<StudioTranscript />} />                 {/* 🆕 02.10 — ST-11 */}
            <Route path="/studio/submissions/:id" element={<StudioSubmission />} />
            <Route path="/studio/quiz/:id" element={<StudioQuiz />} />
            <Route path="/studio/questions/:id" element={<StudioQuestion />} />
            <Route path="/studio/lessons/:id/qa" element={<StudioLessonQa />} />       {/* 🆕 18.10 — 355 (ג) */}
            <Route path="/studio/paths" element={<Paths />} />                       {/* 🆕 01.10 — LP-01 */}
            <Route path="/studio/paths/:id" element={<PathEdit />} />                {/* LP-02 · 03 · 05 */}
            <Route path="/studio/paths/:id/members" element={<PathMembers />} />     {/* LP-04 · 06 */}
          </Route>
          <Route path="*" element={<StaffHome />} />   {/* 🆕 28.09 — מי שיש לה שולחן נוחתת בו */}
        </Routes>
      </Suspense>
    );
  }
  // 🆕 28.09 — מנחה שותפה (D-220): הסטודיו בלבד. 🔑 ההרשאה נאכפת בשרת (הקורסים שלה); כאן רק הניווט.
  if (state.me.role === "facilitator") {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route element={<StaffShell />}>
            <Route path="/studio" element={<StudioQueue />} />
            <Route path="/studio/reflections" element={<StudioReflections />} />
            <Route path="/studio/sent" element={<StudioSent />} />
            <Route path="/studio/courses" element={<StudioCourses />} />
            <Route path="/studio/courses/:id" element={<StudioCourse />} />
            <Route path="/studio/media" element={<StudioMedia />} />
          <Route path="/studio/lessons/:id/transcript" element={<StudioTranscript />} />                 {/* 🆕 02.10 — ST-11 */}
            <Route path="/studio/submissions/:id" element={<StudioSubmission />} />
            <Route path="/studio/quiz/:id" element={<StudioQuiz />} />
            <Route path="/studio/questions/:id" element={<StudioQuestion />} />
            <Route path="/studio/lessons/:id/qa" element={<StudioLessonQa />} />       {/* 🆕 18.10 — 355 (ג) */}
          </Route>
          <Route path="*" element={<Navigate to="/studio" replace />} />
        </Routes>
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/my" element={<MyArea />} />
        <Route path="/my/book" element={<BookSession />} />
        <Route path="/my/booking/:id" element={<ChangeBooking />} />
        <Route path="/my/board" element={<MyBoard />} />              {/* 🆕 01.10 — CA-18 */}
        <Route path="/my/path" element={<MyPath />} />                {/* 🆕 01.10 — LP-07 · 08 */}
        <Route path="/my/passes" element={<MyPasses />} />            {/* 🆕 27.09 — CA-16 */}
        {/* 🆕 27.09 — מרחב הלמידה (LR): שיעור · סיום קורס · תעודות. ⏳ "הקורסים שלי" (AC-02) — הכניסה לכאן */}
        <Route path="/learn/lessons/:id" element={<LearnLesson />} />
        <Route path="/learn/enrollments/:id/done" element={<CourseDone />} />
        <Route path="/learn/enrollments/:id/map" element={<CourseMap />} />   {/* 🆕 28.09 — AC-03 */}
        <Route path="/my/certificates" element={<MyCertificates />} />
        <Route path="/my/partner" element={<MyPartner />} />                {/* 🆕 01.10 — AF-15/16 */}
        <Route path="/my/partner/agreement/:id" element={<MyPartnerAgreement />} />
        <Route path="/my/privacy" element={<Privacy />} />              {/* 🆕 02.10 — AC-09 */}
        <Route path="/my/profile" element={<Profile />} />              {/* 🆕 28.09 — AC-08 */}
        <Route path="/my/courses" element={<MyCourses />} />            {/* 🆕 28.09 — AC-02 */}
        <Route path="/my/purchases" element={<MyPurchases />} />        {/* 🆕 03.10 — AC-04 */}
        <Route path="/site" element={<SiteLogin />} />                  {/* 🆕 04.10 — WP-01 מעבר לאתר מחוברת */}
        <Route path="/my/inbox" element={<Inbox />} />                  {/* 🆕 28.09 — קריאה בלבד (D-216) */}
        <Route path="/my/inbox/:id" element={<Conversation />} />
        <Route path="*" element={<Navigate to="/my" replace />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* 🆕 28.09 — AC-06 אימות תעודה: ציבורי, לפני שער הכניסה (מי שבודקת תעודה אינה משתמשת) */}
          <Route path="/verify/:token" element={<Suspense fallback={<Loading />}><VerifyCertificate /></Suspense>} />
          {/* 🆕 02.10 — AC-09.b הסרה מהדיוור מקישור במייל: ⛔ כניסה, ⛔ סיסמה, ⛔ אישור נוסף (תיקון 40) */}
          <Route path="/unsubscribe/:token" element={<Suspense fallback={<Loading />}><Unsubscribe /></Suspense>} />
          <Route path="/email-change/:token" element={<Suspense fallback={<Loading />}><EmailChange /></Suspense>} />
          {/* 🆕 01.10 — AF-18 קישור הצטרפות פתוח: התנאים לכל מי שמחזיקה בקישור; ההצטרפות — מחוברת בלבד */}
          <Route path="/join/:cid" element={<Suspense fallback={<Loading />}><JoinCampaign /></Suspense>} />
          {/* 🆕 01.10 — CA-17 לוח הפעילויות הציבורי: לכל גולשת, לפני שער הכניסה */}
          <Route path="/board" element={<Suspense fallback={<Loading />}><PublicBoard /></Suspense>} />
          {/* 🆕 06.10 — HM איסוף המלצות: עמוד ציבורי, לפני שער הכניסה. השליחה היא ההסכמה */}
          <Route path="/recommend" element={<Suspense fallback={<Loading />}><Recommend /></Suspense>} />
          <Route path="*" element={<Gate />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
